import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  TouchableOpacity,
  TextInput as RNTextInput,
  Image,
  Alert,
  ScrollView,
  Platform,
} from 'react-native';
import type { TextStyle, ViewStyle } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Text, useTheme } from '@tamagui/core';
import { YStack, XStack } from '@tamagui/stacks';

// Types
import type { RootStackScreenProps } from '@/types/navigation';

// Components
import { SvgBackground } from '@/components';

// API
import { useRequestPhoneVerification } from '@/api/hooks/useAuth';

// Icons
import { Smartphone } from 'lucide-react-native';

// Data
import COUNTRIES, { type CountryDial } from '@/data/countries';

// Constants
import { COLORS, COMMON_STYLES } from '@/constants';

// Utils
import { formatPhoneNumber, getOnlyDigits } from '@/utils';

// Assets
import { logo } from '@/assets';

type Props = RootStackScreenProps<'RegisterStep2'>;

const languageToDefaultCountry: Record<string, CountryDial['code']> = {
  pl: 'PL',
  ua: 'UA',
  ru: 'RU',
  en: 'GB',
};

const RegisterStep2Screen: React.FC<Props> = ({ navigation, route }) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();

  // Get params from navigation
  const userId = route.params?.userId;
  const email = route.params?.email;
  const password = route.params?.password;

  // State
  const [phoneNumber, setPhoneNumber] = useState('');
  const [showCountryList, setShowCountryList] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  // Get default country based on language
  const defaultCountry = useMemo<CountryDial>(() => {
    const lang = i18n.language?.split('-')[0] || 'en';
    const code = languageToDefaultCountry[lang] ?? 'GB';
    return COUNTRIES.find(c => c.code === code) ?? COUNTRIES[0];
  }, [i18n.language]);

  const [country, setCountry] = useState<CountryDial>(defaultCountry);

  useEffect(() => {
    setCountry(defaultCountry);
  }, [defaultCountry]);

  // API Hook
  const { mutate: requestVerification, isPending: isSubmitting } = useRequestPhoneVerification({
    onSuccess: () => {
      setApiError(null);
      const digits = getOnlyDigits(phoneNumber);
      navigation.navigate('RegisterStep3', {
        userId,
        email,
        password,
        phone: digits,
        dialCode: country.dialCode,
      });
    },
    onError: (error) => {
      const errorMessage = error.message || t('errors.phoneVerificationFailed', 'Nie udało się wysłać kodu SMS');
      setApiError(errorMessage);
      Alert.alert(
        t('errors.error', 'Błąd'),
        errorMessage
      );
    },
  });

  // Handlers
  const handleSendCode = useCallback(() => {
    const digits = getOnlyDigits(phoneNumber);
    const isLengthValid = country.validLengths.includes(digits.length);

    if (isSubmitting || !digits || !isLengthValid || !userId) return;

    setApiError(null);
    // Send full phone number with country code
    const fullPhoneNumber = `${country.dialCode}${digits}`;
    requestVerification({
      userId,
      phoneNumber: fullPhoneNumber,
    });
  }, [isSubmitting, phoneNumber, country, userId, requestVerification]);

  const handleCountrySelect = useCallback((selectedCountry: CountryDial) => {
    setCountry(selectedCountry);
    setShowCountryList(false);
  }, []);

  const toggleCountryList = useCallback(() => {
    setShowCountryList(prev => !prev);
  }, []);

  // Computed values
  const digits = getOnlyDigits(phoneNumber);
  const isLengthValid = country.validLengths.includes(digits.length);
  const isFormValid = !!digits && isLengthValid;
  const isWeb = Platform.OS === 'web';

  // Web layout - matching AuthScreen/RegisterScreen design
  if (isWeb) {
    return (
      <ScrollView 
        style={{ flex: 1, backgroundColor: theme.background?.val }} 
        contentContainerStyle={{ flexGrow: 1, paddingHorizontal: 20, paddingVertical: 20, justifyContent: 'space-between' }}
        showsVerticalScrollIndicator={false}
      >
        {/* Main content */}
        <YStack>
          {/* Title */}
          <YStack alignItems="center" marginBottom="$4">
            <Text fontSize={26} fontWeight="500" color="$color" textAlign="center">
              {t('screens.verify.title', 'Weryfikacja')}
            </Text>
            <Text fontSize={13} color="$color10" textAlign="center" marginTop={8} paddingHorizontal="$2">
              {t('screens.verify.subtitle', 'Potwierdź swój numer telefonu')}
            </Text>
          </YStack>

          {/* Icon */}
          <YStack alignItems="center" marginBottom="$4">
            <Smartphone size={48} color={theme.color10?.val} />
          </YStack>

          <Text
            color="$color10"
            fontSize={13}
            textAlign="center"
            marginBottom="$4"
          >
            {t(
              'screens.verify.instruction',
              'Wprowadź swój numer telefonu. Wyślemy Ci kod weryfikacyjny SMS.',
            )}
          </Text>

          {/* Phone Input */}
          <XStack space="$2" width="100%" position="relative" marginBottom="$2">
            <TouchableOpacity onPress={toggleCountryList}>
              <YStack
                backgroundColor={theme.background?.val}
                borderWidth={1}
                borderColor={theme.borderColor?.val ?? '#e5e7eb'}
                borderRadius={10}
                paddingHorizontal={16}
                height={46}
                justifyContent="center"
                minWidth={80}
              >
                <Text color={theme.color?.val} fontSize={13} fontWeight="500">
                  {country.dialCode}
                </Text>
              </YStack>
            </TouchableOpacity>

            {/* Country Dropdown */}
            {showCountryList && (
              <YStack
                position="absolute"
                top={50}
                left={0}
                width={160}
                backgroundColor={theme.background?.val}
                borderWidth={1}
                borderColor={theme.borderColor?.val ?? '#e5e7eb'}
                borderRadius={10}
                zIndex={20}
              >
                {COUNTRIES.map(c => (
                  <TouchableOpacity
                    key={c.code}
                    onPress={() => handleCountrySelect(c)}
                  >
                    <XStack
                      paddingHorizontal={12}
                      paddingVertical={10}
                      justifyContent="space-between"
                    >
                      <Text color={theme.color?.val} fontSize={13}>{c.name}</Text>
                      <Text color={theme.color10?.val} fontSize={13}>{c.dialCode}</Text>
                    </XStack>
                  </TouchableOpacity>
                ))}
              </YStack>
            )}

            <RNTextInput
              value={formatPhoneNumber(phoneNumber)}
              onChangeText={setPhoneNumber}
              keyboardType="phone-pad"
              placeholder={t('screens.verify.phonePlaceholder', '123 456 789')}
              placeholderTextColor={theme.color10?.val}
              style={getWebPhoneInputStyle(theme)}
            />
          </XStack>

          {/* Validation Error */}
          {!isLengthValid && digits.length > 0 && (
            <Text color={COLORS.ERROR} fontSize={10} marginTop={4}>
              {t(
                'screens.verify.lengthError',
                'Nieprawidłowa liczba cyfr dla wybranego kraju',
              )}
            </Text>
          )}

          {/* API Error Display */}
          {apiError && (
            <Text color={COLORS.ERROR} fontSize={11} textAlign="center" marginTop={8}>
              {apiError}
            </Text>
          )}

          <TouchableOpacity
            activeOpacity={0.9}
            disabled={!isFormValid || isSubmitting}
            onPress={handleSendCode}
            style={getWebButtonStyle(!isFormValid || isSubmitting)}
          >
            <Text color="white" fontSize={14} fontWeight="600">
              {t('screens.verify.sendCode', 'Wyślij kod SMS')}
            </Text>
          </TouchableOpacity>
        </YStack>

        {/* Progress Stepper - at bottom */}
        <YStack alignItems="center">
          <XStack space="$2" alignItems="center">
            <YStack width={8} height={8} borderRadius={4} backgroundColor={COLORS.PRIMARY} />
            <YStack width={8} height={8} borderRadius={4} backgroundColor={COLORS.PRIMARY} />
            <YStack width={8} height={8} borderRadius={4} borderWidth={1} borderColor="#ccc" />
          </XStack>
          <Text color="$color10" fontSize={11} marginTop={8}>
            {t('screens.verify.step2', 'Krok 2 z 3')}
          </Text>
        </YStack>
      </ScrollView>
    );
  }

  // Mobile layout - original design
  return (
    <YStack flex={1} backgroundColor="$background">
      {/* Header */}
      <XStack
        paddingTop="$8"
        paddingHorizontal="$4"
        paddingBottom="$3"
        justifyContent="space-between"
        alignItems="center"
        position="relative"
        zIndex={10}
      >
        <XStack space="$3" />
      </XStack>

      {/* Hero Section */}
      <YStack>
        <SvgBackground height={220}>
          <Image
            source={logo}
            style={COMMON_STYLES.logo}
          />

          <Text
            fontSize="$8"
            fontWeight="bold"
            color="white"
            textAlign="center"
            fontFamily="Poppins"
          >
            {t('screens.verify.title', 'Weryfikacja')}
          </Text>

          <Text
            fontSize="$6"
            color="white"
            opacity={0.9}
            textAlign="center"
            fontFamily="Poppins"
            maxWidth="80%"
          >
            {t('screens.verify.subtitle', 'Potwierdź swój numer telefonu')}
          </Text>
        </SvgBackground>
      </YStack>

      {/* Form */}
      <YStack
        paddingHorizontal="$4"
        paddingTop="$4"
        space="$4"
        zIndex={2}
        alignItems="center"
      >
        <YStack paddingTop="$4">
          <Smartphone size={60} color={theme.color10?.val} />
        </YStack>

        <Text
          color="$color10"
          fontSize="$4"
          textAlign="center"
          maxWidth="80%"
          lineHeight={24}
        >
          {t(
            'screens.verify.instruction',
            'Wprowadź swój numer telefonu. Wyślemy Ci kod weryfikacyjny SMS.',
          )}
        </Text>

        {/* Phone Input */}
        <XStack space="$2" width="100%" paddingTop="$2" position="relative">
          <TouchableOpacity onPress={toggleCountryList}>
            <YStack
              backgroundColor={theme.background?.val}
              borderWidth={2}
              borderColor={theme.borderColor?.val ?? '#e5e7eb'}
              borderRadius={12}
              paddingHorizontal={16}
              paddingVertical={16}
              justifyContent="center"
              minWidth={90}
            >
              <Text color={theme.color?.val} fontSize={16} fontWeight="500">
                {country.dialCode}
              </Text>
            </YStack>
          </TouchableOpacity>

          {/* Country Dropdown */}
          {showCountryList && (
            <YStack
              position="absolute"
              top={60}
              left={0}
              width={160}
              backgroundColor={theme.background?.val}
              borderWidth={2}
              borderColor={theme.borderColor?.val ?? '#e5e7eb'}
              borderRadius={12}
              zIndex={20}
            >
              {COUNTRIES.map(c => (
                <TouchableOpacity
                  key={c.code}
                  onPress={() => handleCountrySelect(c)}
                >
                  <XStack
                    paddingHorizontal={12}
                    paddingVertical={10}
                    justifyContent="space-between"
                  >
                    <Text color={theme.color?.val}>{c.name}</Text>
                    <Text color={theme.color10?.val}>{c.dialCode}</Text>
                  </XStack>
                </TouchableOpacity>
              ))}
            </YStack>
          )}

          <RNTextInput
            value={formatPhoneNumber(phoneNumber)}
            onChangeText={setPhoneNumber}
            keyboardType="phone-pad"
            placeholder={t('screens.verify.phonePlaceholder', '123 456 789')}
            placeholderTextColor={theme.color10?.val}
            style={getPhoneInputStyle(theme)}
          />
        </XStack>

        {/* Validation Error */}
        {!isLengthValid && digits.length > 0 && (
          <Text color={COLORS.ERROR} fontSize="$3">
            {t(
              'screens.verify.lengthError',
              'Nieprawidłowa liczba cyfr dla wybranego kraju',
            )}
          </Text>
        )}

        {/* API Error Display */}
        {apiError && (
          <Text color={COLORS.ERROR} fontSize="$3" textAlign="center">
            {apiError}
          </Text>
        )}

        <TouchableOpacity
          activeOpacity={0.9}
          disabled={!isFormValid || isSubmitting}
          onPress={handleSendCode}
          style={getSendCodeButtonStyle(theme, !isFormValid || isSubmitting)}
        >
          <Text color="white" fontSize="$4" fontWeight="600">
            {t('screens.verify.sendCode', 'Wyślij kod SMS')}
          </Text>
        </TouchableOpacity>

        {/* Progress Indicator */}
        <YStack
          alignItems="center"
          space="$2"
          paddingTop="$6"
          paddingBottom="$6"
        >
          <Text color="$color10" fontSize="$3">
            {t('screens.verify.step2', 'Krok 2 z 3')}
          </Text>
          <XStack space="$2">
            <YStack
              width={8}
              height={8}
              borderRadius={4}
              backgroundColor="$color8"
            />
            <YStack
              width={8}
              height={8}
              borderRadius={4}
              backgroundColor="$color10"
            />
            <YStack
              width={8}
              height={8}
              borderRadius={4}
              borderWidth={1}
              borderColor="$color8"
            />
          </XStack>
        </YStack>
      </YStack>
    </YStack>
  );
};

// Web styles
function getWebPhoneInputStyle(theme: ReturnType<typeof useTheme>): TextStyle {
  return {
    flex: 1,
    height: 46,
    borderWidth: 1,
    borderColor: theme.borderColor?.val ?? '#e5e7eb',
    borderRadius: 10,
    paddingHorizontal: 16,
    fontSize: 13,
    color: theme.color?.val,
    backgroundColor: theme.background?.val,
    fontFamily: 'Poppins',
  };
}

function getWebButtonStyle(disabled: boolean): ViewStyle {
  return {
    height: 46,
    borderRadius: 10,
    backgroundColor: disabled ? COLORS.DISABLED : COLORS.PRIMARY,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 16,
  };
}

// Mobile styles
function getPhoneInputStyle(theme: ReturnType<typeof useTheme>): TextStyle {
  return {
    ...COMMON_STYLES.input,
    flex: 1,
    borderColor: theme.borderColor?.val ?? '#e5e7eb',
    color: theme.color?.val,
    backgroundColor: theme.background?.val,
  };
}

function getSendCodeButtonStyle(
  theme: ReturnType<typeof useTheme>,
  disabled: boolean,
): ViewStyle {
  return {
    ...COMMON_STYLES.button,
    backgroundColor: disabled ? COLORS.DISABLED : COLORS.PRIMARY,
    width: '100%',
  };
}

export default RegisterStep2Screen;
